const emitCompareOutcome = (name, detail = {}) => document.dispatchEvent(new CustomEvent('cgau:commerce', { detail: { name, source: 'compare', ...detail } }));
const STORAGE_KEY = 'cgau-shortlist-v2';
const ROWS = [
  ['Product price', 'price'], ['Availability', 'availability'], ['Condition', 'condition'],
  ['Resolution', 'resolution'], ['Power', 'power'], ['Recording mode', 'recording'],
  ['Storage', 'storage'], ['System', 'systems'], ['Compatible hubs', 'hubs'],
  ['Required extras', 'requirement'], ['Compatibility notes', 'compatibilityNote'],
];
const knownPrice = (value) => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const valueText = (value) => (Array.isArray(value) ? value.filter(Boolean).join(', ') : String(value ?? '').trim());
const productPrice = (product) => {
  if (!knownPrice(product.price)) return 'Price check needed';
  try {
    return `${product.priceVaries ? 'From ' : ''}${new Intl.NumberFormat('en-AU', { style: 'currency', currency: product.currency || 'AUD' }).format(product.price / 100)}`;
  } catch { return 'Price check needed'; }
};
const rowValue = (product, key) => {
  if (product.refreshFailed) return 'Could not refresh. Check the product page.';
  if (key === 'price') return productPrice(product);
  if (key === 'availability') return product.available === true ? 'Available to buy' : product.available === false ? 'Sold out' : 'Availability check needed';
  return valueText(product[key]) || (key === 'requirement' ? 'Not verified. Check required parts before buying.' : 'Not recorded. Check the product page.');
};
const orderedRows = (products) => ROWS.map(([label, key], index) => ({ label, key, index,
  different: new Set(products.map((product) => rowValue(product, key))).size > 1,
})).sort((a, b) => Number(b.different) - Number(a.different) || a.index - b.index);
const safeProductUrl = (value, origin) => {
  try {
    const url = new URL(value, origin);
    if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+$/i.test(url.pathname)) return null;
    url.search = ''; url.hash = '';
    return url;
  } catch { return null; }
};
const selectionFrom = (value, origin) => {
  const url = safeProductUrl(value.url || (value.handle ? `/products/${value.handle}` : ''), origin);
  if (!url || !/^\d+$/.test(String(value.id || ''))) return null;
  return { id: String(value.id), url: url.pathname, handle: url.pathname.split('/').pop(), title: String(value.title || 'Saved product').slice(0, 220) };
};
const ComparisonBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
class CgauProductComparison extends ComparisonBase {
  constructor() { super(); this.selected = new Map(); this.refreshVersion = 0; }
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.tray = this.querySelector('[data-compare-tray]'); this.count = this.querySelector('[data-compare-count]');
    this.dialog = this.querySelector('[data-compare-dialog]'); this.table = this.querySelector('[data-compare-table]');
    this.mobile = this.querySelector('[data-compare-mobile]'); this.status = this.querySelector('[data-compare-status]');
    this.restore();
    document.addEventListener('click', this.handleDocumentClick);
    this.addEventListener('click', this.handleControls);
    this.dialog?.addEventListener('close', this.cancelRefresh);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.cancelRefresh);
    this.observer = new MutationObserver((records) => {
      if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-cgau-compare]') || n.querySelector?.('[data-cgau-compare]'))))) this.sync();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.sync();
  }
  disconnectedCallback() {
    this.connected = false; this.cancelRefresh(); this.observer?.disconnect(); clearTimeout(this.limitTimer);
    document.removeEventListener('click', this.handleDocumentClick); this.removeEventListener('click', this.handleControls);
    this.dialog?.removeEventListener('close', this.cancelRefresh); window.removeEventListener('pageshow', this.handlePageShow); window.removeEventListener('pagehide', this.cancelRefresh);
  }
  handlePageShow = () => { this.restore(); this.sync(); if (this.dialog?.open) void this.openComparison(); };
  cancelRefresh = () => { this.refreshVersion++; this.abort?.abort(); };
  restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      this.selected = new Map((Array.isArray(saved) ? saved : []).slice(0, 3).map((item) => selectionFrom(item, location.origin)).filter(Boolean).map((item) => [item.id, item]));
    } catch { /* Storage may be blocked. The current page shortlist still works. */ }
  }
  persist() {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...this.selected.values()])); }
    catch { /* Functional in-memory fallback, without cookies or tracking. */ }
  }
  handleControls = (event) => {
    if (event.target.closest('[data-compare-open]')) void this.openComparison();
    if (event.target.closest('[data-compare-clear]')) this.clear();
    if (event.target.closest('[data-compare-close]')) this.dialog?.close();
    const remove = event.target.closest('[data-compare-remove]');
    if (remove) { this.selected.delete(remove.dataset.compareRemove); this.persist(); this.sync(); if (this.selected.size < 2) this.dialog?.close(); else { this.querySelector('[data-compare-close]')?.focus(); void this.openComparison(); } }
  };
  handleDocumentClick = (event) => {
    const button = event.target.closest('[data-cgau-compare]');
    if (!button) return;
    const item = selectionFrom(button.dataset, location.origin);
    if (!item) return;
    event.preventDefault(); event.stopPropagation();
    if (this.selected.has(item.id)) this.selected.delete(item.id);
    else if (this.selected.size < 3) this.selected.set(item.id, item);
    else { this.flashLimit(); return; }
    this.persist(); this.sync();
  };
  sync() {
    const size = this.selected.size;
    if (this.tray) this.tray.hidden = size === 0;
    if (this.count) this.count.textContent = `${size} of 3 selected · saved in this tab`;
    const open = this.querySelector('[data-compare-open]'); if (open) open.disabled = size < 2;
    document.querySelectorAll('[data-cgau-compare]').forEach((button) => {
      const added = this.selected.has(button.dataset.id);
      button.setAttribute('aria-pressed', String(added));
      const text = added ? 'Added to compare ✓' : 'Compare';
      if (button.textContent.trim() !== text) button.textContent = text;
    });
  }
  flashLimit() {
    clearTimeout(this.limitTimer);
    if (this.count) this.count.textContent = 'Maximum 3. Remove one or clear the shortlist first.';
    this.limitTimer = setTimeout(() => this.sync(), 2200);
  }
  clear() { this.selected.clear(); this.persist(); this.cancelRefresh(); this.dialog?.close(); this.sync(); }
  async fetchProduct(item, signal) {
    const url = safeProductUrl(item.url, location.origin);
    if (!url) throw new Error('Invalid product route');
    url.searchParams.set('section_id', 'cgau-comparison-data');
    const response = await fetch(url, { signal, cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Product unavailable');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const product = JSON.parse(doc.querySelector('[data-compare-product]')?.textContent || 'null');
    if (!product || String(product.id) !== item.id) throw new Error('Product response mismatch');
    return { ...product, id: String(product.id), url: item.url };
  }
  async openComparison() {
    if (this.selected.size < 2 || !this.dialog || !this.table) return;
    this.cancelRefresh();
    const version = this.refreshVersion;
    this.abort = new AbortController();
    const controller = this.abort;
    const timeout = setTimeout(() => controller.abort(), 7000);
    const items = [...this.selected.values()];
    this.table.replaceChildren(); this.mobile?.replaceChildren();
    this.status.textContent = 'Refreshing current prices, availability and product details…';
    if (!this.dialog.open) { this.dialog.showModal(); emitCompareOutcome('compare_opened', { quantity: items.length }); }
    try {
      const products = await Promise.all(items.map(async (item) => {
        try { return await this.fetchProduct(item, controller.signal); }
        catch { return { ...item, refreshFailed: true }; }
      }));
      if (version !== this.refreshVersion || !this.isConnected) return;
      this.render(products);
      const failed = products.filter((product) => product.refreshFailed).length;
      this.status.textContent = failed ? `${failed} product ${failed === 1 ? 'check failed' : 'checks failed'}. Unverified prices and details are withheld. Try reopening, or check the product pages.` : 'Prices, availability and product details refreshed. Check the exact listing before buying.';
    } finally { clearTimeout(timeout); }
  }
  render(products) {
    const rows = orderedRows(products);
    const head = document.createElement('thead'), header = document.createElement('tr');
    const corner = document.createElement('th'); corner.scope = 'col'; corner.textContent = 'Differences first'; header.append(corner);
    products.forEach((product) => { const cell = document.createElement('th'); cell.scope = 'col'; cell.append(this.productHead(product)); header.append(cell); });
    head.append(header);
    const body = document.createElement('tbody');
    rows.forEach(({ label, key, different }) => {
      const row = document.createElement('tr'); row.classList.toggle('cgau-compare-difference', different);
      const heading = document.createElement('th'); heading.scope = 'row'; heading.textContent = label; row.append(heading);
      products.forEach((product) => { const cell = document.createElement('td'); cell.textContent = rowValue(product, key); row.append(cell); });
      body.append(row);
    });
    this.table.replaceChildren(head, body);
    this.mobile?.replaceChildren(...products.map((product) => {
      const article = document.createElement('article'); article.className = 'cgau-compare-summary'; article.append(this.productHead(product));
      const list = document.createElement('dl');
      rows.forEach(({ label, key, different }) => {
        const pair = document.createElement('div'); pair.classList.toggle('cgau-compare-difference', different);
        const term = document.createElement('dt'), detail = document.createElement('dd');
        term.textContent = `${label}${different ? ' · differs' : ''}`; detail.textContent = rowValue(product, key); pair.append(term, detail); list.append(pair);
      }); article.append(list); return article;
    }));
  }
  productHead(product) {
    const wrapper = document.createElement('div'); wrapper.className = 'cgau-compare-product';
    if (product.image) { const image = document.createElement('img'); image.src = product.image; image.alt = ''; image.loading = 'lazy'; image.width = 150; image.height = 150; wrapper.append(image); }
    const link = document.createElement('a'); link.href = safeProductUrl(product.url, location.origin)?.pathname || '/collections/all'; link.textContent = product.title || 'Product';
    if (/^\d+$/.test(String(product.id))) link.addEventListener('click', () => emitCompareOutcome('compare_product_clicked', { product_id: String(product.id) }));
    wrapper.append(link);
    const button = document.createElement('button'); button.type = 'button'; button.dataset.compareRemove = String(product.id); button.textContent = 'Remove'; button.setAttribute('aria-label', `Remove ${product.title || 'product'} from comparison`); wrapper.append(button);
    return wrapper;
  }
}
export { knownPrice, productPrice, rowValue, orderedRows, safeProductUrl, selectionFrom };
if (typeof customElements !== 'undefined' && !customElements.get('cgau-product-comparison')) customElements.define('cgau-product-comparison', CgauProductComparison);
